import React, { createContext, useState, useContext, useEffect } from 'react';
import MediabankDialog, { MediabankDialogMode } from './BizMediabankDialog';
import {
	MediabankFile,
	MediabankFileType,
	MediabankFileTypes,
	MediabankSettings,
	MediabankUploadLocation,
} from '../../Types/mediabank';
import { useGlobalState } from '../../Contexts/globalStateContext';
import { MediaContentCategory } from '../../Types/mediaCatalog';
import { useTranslation } from 'react-i18next';

export type SelectedFileCallback = (selectedFile: MediabankFile | null | undefined) => void;

type OpenMediabankOptions = {
	callback: SelectedFileCallback;
	fileTypes: MediabankFileType[];
	uploadLocation: MediabankUploadLocation;
	settings?: MediabankSettings;
	mode?: MediabankDialogMode;
	initialCategory?: MediaContentCategory;
};

type BizMediabankContextType = {
	openMediabank: (options: OpenMediabankOptions, legacy?: boolean) => void;
	closeMediabank: SelectedFileCallback;
};

const BizMediabankContext = createContext<BizMediabankContextType | null>(null);

type BizMediabankProviderProps = {
	children: React.ReactNode;
};

export const BizMediabankProvider = ({ children }: BizMediabankProviderProps) => {
	const { t } = useTranslation();
	const [isMediabankOpen, setMediabankOpen] = useState(false);
	const { showModal, hideModal, state } = useGlobalState();

	const [config, setConfig] = useState<{
		options: OpenMediabankOptions;
		legacy?: boolean;
	}>({
		options: {
			callback: () => {},
			fileTypes: [MediabankFileTypes.Undetermined],
			uploadLocation: MediabankUploadLocation.Undefined,
			settings: {},
		},
	});

	const getModalTitle = () => {
		if (config.options.mode === 'UPLOAD') {
			return t('MEDIABANK.UPLOAD_FILE');
		} else if (config.options.mode === 'SELECT') {
			return t('MEDIABANK.SELECT_FILE');
		} else if (config.options.mode === 'UNSPLASH') {
			return t('MEDIABANK.BROWSE_UNSPLASH');
		}
		return t('MEDIABANK.TITLE');
	};

	useEffect(() => {
		if (isMediabankOpen) {
			showModal(
				<MediabankDialog
					onClose={closeMediabank}
					fileTypes={config.options.fileTypes}
					uploadLocation={config.options.uploadLocation}
					settings={config.options.settings}
					mode={config.options.mode}
					legacy={config.legacy}
					initialCategory={config.options.initialCategory}
				/>,
				getModalTitle(),
				() => closeMediabank(undefined),
				Boolean(state.adminState)
			);
		} else {
			hideModal();
		}
	}, [isMediabankOpen, config]);

	const openMediabank = (options: OpenMediabankOptions, legacy?: boolean) => {
		setConfig((current) => {
			return {
				...current,
				options,
				legacy,
			};
		});

		setMediabankOpen(true);
	};

	const closeMediabank: SelectedFileCallback = (selectedFile: MediabankFile | null | undefined) => {
		setMediabankOpen(false);
		hideModal();
		config.options.callback(selectedFile);
	};

	return <BizMediabankContext.Provider value={{ openMediabank, closeMediabank }}>{children}</BizMediabankContext.Provider>;
};

export const useBizMediabank = () => {
	const context = useContext(BizMediabankContext);
	if (!context) {
		throw new Error('useBizMediabank must be used within a BizMediabankProvider');
	}
	return context;
};
